var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import DataCacheService from '@/shared/services/DataCacheService';
import { listItemMsg } from '@/shared/components/FormattedListValue';
var messages = defineMessages({
    ShowAll: { id: 'Show all', defaultMessage: 'Show all' },
});
var CountryFlag = function (_a) {
    var code = _a.code;
    return (_jsx("div", { className: "iti__flag".concat(code ? " iti__".concat(code.toLowerCase()) : ''), style: { display: 'inline-block' } }));
};
var TYPEAHEAD_LIMIT = 10;
var CountrySelect = function (_a) {
    var value = _a.value, onChange = _a.onChange, placeholder = _a.placeholder, _b = _a.disabled, disabled = _b === void 0 ? false : _b;
    var intl = useIntl();
    var _c = useState(''), inputValue = _c[0], setInputValue = _c[1];
    var _d = useState(false), isOpen = _d[0], setIsOpen = _d[1];
    var _e = useState(false), showAll = _e[0], setShowAll = _e[1];
    var _f = useState(false), isTyping = _f[0], setIsTyping = _f[1];
    var containerRef = useRef(null);
    var allCountries = useMemo(function () {
        var _a;
        var countries = ((_a = DataCacheService.getDataCache()) === null || _a === void 0 ? void 0 : _a.country) || [];
        return countries.map(function (c) { return ({
            key: c.code,
            label: intl.formatMessage(listItemMsg('country', c.code, c.name)),
            isCommon: c.isCommon,
        }); });
    }, [intl]);
    var commonCountries = useMemo(function () { return allCountries.filter(function (c) { return c.isCommon; }).sort(function (a, b) { return a.label.localeCompare(b.label); }); }, [allCountries]);
    var allCountriesSorted = useMemo(function () { return __spreadArray([], allCountries, true).sort(function (a, b) { return a.label.localeCompare(b.label); }); }, [allCountries]);
    var selectedCountry = value ? allCountries.find(function (c) { return c.key === value; }) || null : null;
    useEffect(function () {
        if (!isTyping) {
            setInputValue(selectedCountry ? selectedCountry.label : '');
        }
    }, [selectedCountry, isTyping]);
    var typeaheadResults = useMemo(function () {
        if (!inputValue)
            return [];
        var search = inputValue.toUpperCase();
        return allCountries
            .filter(function (c) { return c.label.toUpperCase().includes(search); })
            .sort(function (a, b) {
            if (a.isCommon !== b.isCommon)
                return a.isCommon ? -1 : 1;
            return a.label.localeCompare(b.label);
        })
            .slice(0, TYPEAHEAD_LIMIT);
    }, [inputValue, allCountries]);
    var selectItem = useCallback(function (item) {
        onChange(item.key);
        setInputValue(item.label);
        setIsOpen(false);
        setIsTyping(false);
        setShowAll(false);
    }, [onChange]);
    var handleInputChange = useCallback(function (e) {
        setInputValue(e.target.value);
        setIsTyping(true);
        setIsOpen(true);
    }, []);
    var handleInputFocus = useCallback(function () {
        if (selectedCountry) {
            setInputValue(selectedCountry.label);
        }
    }, [selectedCountry]);
    var handleBlur = useCallback(function (e) {
        var _a;
        if ((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.relatedTarget))
            return;
        setIsOpen(false);
        setIsTyping(false);
        setShowAll(false);
        if (!inputValue) {
            onChange(null);
            return;
        }
        var match = allCountries.find(function (c) { return c.label.toUpperCase() === inputValue.toUpperCase(); });
        if (match) {
            selectItem(match);
        }
        else if (selectedCountry) {
            setInputValue(selectedCountry.label);
        }
        else {
            onChange(null);
            setInputValue('');
        }
    }, [inputValue, allCountries, selectedCountry, onChange, selectItem]);
    var toggleDropdown = useCallback(function () {
        if (disabled)
            return;
        setIsTyping(false);
        setIsOpen(function (prev) {
            if (!prev)
                setShowAll(false);
            return !prev;
        });
    }, [disabled]);
    var handleShowAll = useCallback(function (e) {
        e.preventDefault();
        e.stopPropagation();
        setShowAll(true);
    }, []);
    var renderItem = function (item) { return (_jsx("li", __assign({ className: (selectedCountry === null || selectedCountry === void 0 ? void 0 : selectedCountry.key) === item.key ? 'selected' : undefined }, { children: _jsxs("a", __assign({ href: "#", onMouseDown: function (e) {
                e.preventDefault();
                selectItem(item);
            } }, { children: [_jsx("div", __assign({ className: "flag-box" }, { children: _jsx(CountryFlag, { code: item.key }) })), _jsx("span", __assign({ className: "country-name" }, { children: item.label }))] })) }), item.key)); };
    var defaultPlaceholder = intl.formatMessage(listItemMsg('country', null, ''));
    var renderDropdownContent = function () {
        if (isTyping) {
            if (typeaheadResults.length === 0)
                return null;
            return typeaheadResults.map(renderItem);
        }
        return (_jsxs(_Fragment, { children: [commonCountries.map(function (item) { return renderItem(item); }), _jsx("li", { role: "separator", className: "divider" }), showAll ? (allCountriesSorted.map(renderItem)) : (_jsx("li", { children: _jsx("a", __assign({ href: "#", onMouseDown: handleShowAll }, { children: _jsx("span", { children: intl.formatMessage(messages.ShowAll) }) })) }))] }));
    };
    return (_jsxs("div", __assign({ ref: containerRef, className: "dropdown", onBlur: handleBlur }, { children: [_jsxs("div", __assign({ className: "input-group" }, { children: [_jsx("span", __assign({ className: "input-group-addon", style: { lineHeight: 1 } }, { children: _jsx(CountryFlag, { code: value }) })), _jsx("input", { type: "text", className: "form-control", value: inputValue, onChange: handleInputChange, onFocus: handleInputFocus, placeholder: placeholder || defaultPlaceholder, disabled: disabled, autoComplete: "off" }), _jsx("span", __assign({ className: "input-group-btn" }, { children: _jsx("button", __assign({ type: "button", className: "btn btn-default dropdown-toggle btn-font-14", onClick: toggleDropdown, disabled: disabled, tabIndex: -1 }, { children: _jsx("span", { className: "caret" }) })) }))] })), isOpen && (_jsx("ul", __assign({ className: "dropdown-menu", role: "menu", style: { display: 'block', maxHeight: 200, overflowY: 'auto', width: '100%' } }, { children: renderDropdownContent() })))] })));
};
export default CountrySelect;
